// Cinematic dark pill strip — last-chance path picker before the footer.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const PILLS = [
  { key: 'career-clarity', label: 'After 10th / Inter', to: '/career-clarity', color: '#f59e0b' },
  { key: 'specializations', label: 'Inner Specializations', to: '/specializations', color: '#14b8a6' },
  { key: 'job-roles', label: 'Future-Ready Job Roles', to: '/job-roles', color: '#3b82f6' },
  { key: 'ai-pathway', label: 'Future AI Agent Pathway', to: '/ai-pathway', color: '#6366f1' },
  { key: 'banking', label: 'Banking Careers', to: '/banking', color: '#10b981' },
  { key: 'blue-collar', label: 'Blue-Collar Jobs', to: '/blue-collar', color: '#f97316' },
  { key: 'build-badges', label: 'Build Badges', to: '/build-badges', color: '#eab308' },
  { key: 'ai-risk-check', label: 'Is AI Coming for Your Role?', to: '/ai-risk-check', color: '#0ea5e9' },
  { key: 'ownpath', label: 'Entrepreneurship · OwnPath', to: '/ownpath', color: '#f43f5e' },
  { key: 'life-skills', label: 'Life Skills — Free', to: '/life-skills', color: '#84cc16' },
  { key: 'internships', label: 'Internship Pathfinder', to: '/pathways/internships', color: '#06b6d4' },
  { key: 'fresher-launchpad', label: 'I\u2019m starting my career', to: '/pathways/fresher-launchpad', color: '#10b981' },
  { key: 'career-switch-growth', label: 'I\u2019m switching or growing', to: '/pathways/career-switch-growth', color: '#8b5cf6' },
  { key: 'the-ai-shift', label: 'The AI Shift \u2014 Prepare Now', to: '/the-ai-shift', color: '#0d9488' },
];

// Deliberate 4 + 4 + 5 rows so no pill sits alone; each row centres itself
// and still wraps cleanly on small screens.
const PILL_ROWS = [PILLS.slice(0, 5), PILLS.slice(5, 10), PILLS.slice(10, 14)];

const PathPillStrip = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-16 md:py-20 px-4 md:px-6 overflow-hidden border-t border-white/10"
      style={{ background: '#0F172A' }}
      data-testid="path-pill-strip"
    >
      <style>{`
        @keyframes ppsIn {
          0%   { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .pps-item {
          opacity: 0;
        }
        .pps-item.pps-in {
          animation: ppsIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .pps-pill {
          transition: box-shadow 0.3s ease, background-color 0.3s ease, transform 0.3s ease;
        }
        .pps-pill:hover, .pps-pill:active {
          box-shadow: 0 0 22px 2px var(--pps-glow);
          transform: translateY(-2px);
        }
        @media (prefers-reduced-motion: reduce) {
          .pps-item, .pps-item.pps-in { opacity: 1; animation: none; }
          .pps-pill:hover, .pps-pill:active { transform: none; }
        }
      `}</style>

      {/* Faint cinematic glows */}
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute -top-24 left-1/4 w-80 h-80 bg-teal-500 rounded-full filter blur-3xl" />
        <div className="absolute -bottom-24 right-1/5 w-72 h-72 bg-amber-500 rounded-full filter blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto">
        <h2
          className="text-2xl md:text-4xl font-bold text-white tracking-tight text-center"
          data-testid="path-pill-strip-headline"
        >
          Still deciding? Pick your starting point.
        </h2>

        <div className="mt-8 md:mt-10 space-y-2.5 sm:space-y-3" data-testid="path-pill-strip-pills">
          {PILL_ROWS.map((row, rIdx) => (
            <div key={rIdx} className="flex flex-wrap justify-center gap-2.5 sm:gap-3">
              {row.map((p, i) => {
                const idx = rIdx * 4 + i;
                return (
                  <span
                    key={p.key}
                    className={`pps-item ${inView ? 'pps-in' : ''}`}
                    style={{ animationDelay: `${idx * 60}ms` }}
                  >
                    <Link
                      to={p.to}
                      data-testid={`path-pill-${p.key}`}
                      className="pps-pill inline-flex items-center min-h-[44px] px-3.5 py-2 text-[12.5px] sm:px-5 sm:py-2.5 sm:text-sm rounded-full font-semibold text-white/90 bg-white/5"
                      style={{ border: `1.5px solid ${p.color}`, '--pps-glow': `${p.color}55` }}
                    >
                      {p.label}
                    </Link>
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PathPillStrip;
